import React from 'react';
import { ArrowRightIcon, SuccessfulIcon } from 'cokitchen-icons';
import { Link } from 'react-router-dom';
import { ReactComponent as CompanyIcon } from 'assets/icons/company.svg';
import { ReactComponent as DepartmentIcon } from 'assets/icons/department.svg';
import { ReactComponent as TeamIcon } from 'assets/icons/team.svg';
import { ReactComponent as AdminIcon } from 'assets/icons/admin.svg';
import { ReactComponent as WalletIcon } from 'assets/icons/wallet.svg';
import { CalendarIcon, LocationIcon } from 'cokitchen-icons';
import { ROUTE_KEYS } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';
import { ENUM_ONBOARDING_STATUS, ENUM_ONBOARDING_STEP } from 'utils/enum';

const ONBOARDING_STEPS = [
  {
    step: ENUM_ONBOARDING_STEP.ADD_COMPANY_INFO,
    icon: <CompanyIcon className='w-[21px] h-[21px]' />,
    title: 'Company info',
    route: '',
    description: 'Enter your company details for a personalized experience.'
  },
  {
    step: ENUM_ONBOARDING_STEP.ADD_BRANCH_ADDRESSES,
    icon: <LocationIcon className='w-[21px] h-[21px]' />,
    title: 'Add your branch addresses',
    route: `${ROUTE_KEYS.GET_STARTED_ACTION}/${ROUTE_KEYS.ADD_BRANCH_ADDRESSES}`,
    description: 'Add branch addresses to serve as delivery locations'
  },
  {
    step: ENUM_ONBOARDING_STEP.SET_OPERATIONAL_DAYS,
    icon: <CalendarIcon className='w-[21px] h-[21px]' />,
    title: 'Set office operating days',
    route: `${ROUTE_KEYS.GET_STARTED_ACTION}/${ROUTE_KEYS.SET_OPERATING_DAYS}`,
    description: 'Select office opening and closing days.'
  },
  {
    step: ENUM_ONBOARDING_STEP.ADD_DEPARTMENT,
    icon: <DepartmentIcon className='w-[21px] h-[21px]' />,
    title: 'Add department',
    route: `${ROUTE_KEYS.GET_STARTED_ACTION}/${ROUTE_KEYS.ADD_DEPARTMENT}`,
    description: 'Add a department for grouping.'
  },
  {
    step: ENUM_ONBOARDING_STEP.ADD_MEMBER,
    icon: <TeamIcon className='w-[21px] h-[21px]' />,
    title: 'Add your team',
    route: `${ROUTE_KEYS.GET_STARTED_ACTION}/${ROUTE_KEYS.ADD_YOUR_TEAM}`,
    description: 'Invite team members to join and enjoy Foodcourt Business.'
  },
  {
    step: ENUM_ONBOARDING_STEP.TOP_UP_WALLET,
    icon: <WalletIcon className='w-[21px] h-[21px]' />,
    title: 'Top up wallet',
    route: `${ROUTE_KEYS.GET_STARTED_ACTION}/${ROUTE_KEYS.TOP_UP_WALLET}`,
    description: 'Fund your company wallet to start budgeting and gifting.'
  },
  {
    step: ENUM_ONBOARDING_STEP.ASSIGN_ADMIN,
    icon: <AdminIcon className='w-[21px] h-[21px]' />,
    title: 'Assign admin',
    route: '?assign_admin=true',
    description: 'Designate an admin for account management.'
  }
];

const GetStartedCard = () => {
  const { onboarding } = useDashboardContext();

  const isStepCompleted = (step: ENUM_ONBOARDING_STEP) => {
    const onboardingStep = onboarding.find((o) => o.step === step);
    if (!onboardingStep) return false;
    return [ENUM_ONBOARDING_STATUS.COMPLETED, ENUM_ONBOARDING_STATUS.SKIPPED].includes(
      onboardingStep.status
    );
  };

  return (
    <div className='border border-gray-14 w-full py-2 rounded-md'>
      {ONBOARDING_STEPS.map(({ route, description, icon, title, step }, i) => {
        return (
          <Link
            key={i}
            to={`${route}`}
            className={`flex items-center py-4 px-4 gap-2 sm:gap-5 ${
              isStepCompleted(step) ? 'cursor-default' : 'cursor-pointer'
            } ${i !== ONBOARDING_STEPS.length - 1 ? 'border-b border-gray-14' : ''}
            ${
              i && !isStepCompleted(ONBOARDING_STEPS[i - 1]['step'])
                ? '!cursor-not-allowed opacity-50'
                : ''
            }
            `}
            onClick={(e) => {
              if (i > 0 && !isStepCompleted(ONBOARDING_STEPS[i - 1]['step'])) {
                e.preventDefault();
              }
            }}>
            <div className='w-[45px] h-[45px] flex justify-center items-center rounded-full border border-gray-14 text-red-5'>
              {icon}
            </div>
            <div className='flex flex-col grow mr-1 w-[55%]'>
              <label className='font-medium text-gray-10'>{title}</label>
              <p className='text-gray-9 text-xs'>{description}</p>
            </div>
            {isStepCompleted(step) ? (
              <SuccessfulIcon className='w-[30px] h-[30px] text-green-8 ' />
            ) : (
              <ArrowRightIcon className='bg-[#F2F2F3] p-1 w-[24px] h-[24px] text-gray-9 rounded-full hover:bg-gray-4 hover:text-white transition-all' />
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default GetStartedCard;
